import { useState, Dispatch, SetStateAction } from 'react';
import {
  IconButton,
  Box,
  Grid,
  Button,
  Container,
  useMediaQuery,
  Typography,
  type Theme,
} from '@mui/material';
import { Icon24UtilityClose } from '@care/react-icons';
import { clearFilters, saveFilters, loadFilters } from '@/utils/filtersStorage';
import {
  ChildCareAgeGroups,
  SEOChildCareQualifications,
  SEOSegmentationFilterInput,
  SEOSegmentationSearchingFor,
} from '@/__generated__/globalTypes';
import { trackAmplitudeEvent, AmplitudeEvent } from '@/lib/analyticsHelper';
import { ServiceIdForMember } from '@/constants';
import { useSegmentationFiltersContext } from '../contexts/SegmentationFiltersContext';
import { SeniorCareFilters } from './Verticals/SeniorCare/SeniorCareFilters';
import { PetCareFilters } from './Verticals/PetCare/PetCareFilters';
import { ChildCareFilters } from './Verticals/ChildCare/ChildCareFilters';
import { getAmplitudeFilters } from './utils/getAmplitudeFilters';
import { usePageAttributes } from '../contexts/PageAttributesContext';
import { HomeCareFilters } from './Verticals/HomeCare/HomeCareFilters';

interface FiltersProps {
  setPopoverAnchorEl?: Dispatch<SetStateAction<(EventTarget & Element) | null>>;
  setOpenFiltersPopover?: Dispatch<SetStateAction<boolean>>;
  setOpenFiltersModal?: Dispatch<SetStateAction<boolean>>;
}

type ProfessionalSkillsType = Pick<
  SEOSegmentationFilterInput,
  'certifiedNursingAssistant' | 'seniorCareCprTrained' | 'homeHealthAid' | 'registeredNurse'
>;

type PreferencesType = Pick<
  SEOSegmentationFilterInput,
  'comfortableWithPets' | 'hasCar' | 'nonSmoker'
>;

const Filters = ({
  setPopoverAnchorEl,
  setOpenFiltersPopover,
  setOpenFiltersModal,
}: FiltersProps) => {
  const localStorageFilters = loadFilters();
  const { fetchResults, filters } = useSegmentationFiltersContext();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { searchingFor, serviceIdForMember } = usePageAttributes();

  const professionalSkillsOptions: ProfessionalSkillsType = {
    certifiedNursingAssistant: false,
    seniorCareCprTrained: false,
    homeHealthAid: false,
    registeredNurse: false,
  };
  const preferencesOptions: PreferencesType = {
    comfortableWithPets: false,
    hasCar: false,
    nonSmoker: false,
  };

  const [hourlyRate, setHourlyRate] = useState<[number, number]>([10, 50]);
  const [seniorCareSubServices, setSeniorCareSubServices] = useState<
    SEOSegmentationFilterInput['seniorCareSubServices']
  >(localStorageFilters?.seniorCareSubServices || []);
  const [seniorCareProfessionalSkillsObject, setSeniorCareProfessionalSkillsObject] =
    useState<ProfessionalSkillsType>(professionalSkillsOptions);
  const [preferencesObject, setPreferencesObject] = useState<PreferencesType>(preferencesOptions);
  const [languagesSpoken, setLanguagesSpoken] = useState<SEOSegmentationFilterInput['languages']>(
    localStorageFilters?.languages || []
  );
  const [numberOfChildren, setNumberOfChildren] = useState<number>(
    localStorageFilters?.numberOfChildren ?? 1
  );
  const [childCareAgeGroupsList, setChildCareAgeGroupsList] = useState<ChildCareAgeGroups[]>(
    localStorageFilters?.childCareAgeGroups || []
  );
  const [childCareProfessionalSkillsList, setChildCareProfessionalSkillsList] = useState<
    SEOChildCareQualifications[]
  >(localStorageFilters?.childCareQualifications || []);
  const [housekeepingSubServices, setHousekeepingSubServices] = useState<
    SEOSegmentationFilterInput['housekeepingSubServices']
  >(localStorageFilters?.housekeepingSubServices || []);

  const showPetCareUserFilters =
    searchingFor === SEOSegmentationSearchingFor.PROVIDER &&
    serviceIdForMember === ServiceIdForMember.PET_CARE;

  const showSeniorCareUserFilters =
    searchingFor === SEOSegmentationSearchingFor.PROVIDER &&
    serviceIdForMember === ServiceIdForMember.SENIOR_CARE;

  const showChildCareUserFilters =
    (searchingFor === SEOSegmentationSearchingFor.PROVIDER ||
      searchingFor === SEOSegmentationSearchingFor.ALL_PROVIDER) &&
    (serviceIdForMember === ServiceIdForMember.CHILD_CARE ||
      serviceIdForMember === ServiceIdForMember.BABYSITTER ||
      serviceIdForMember === ServiceIdForMember.NANNY);

  const showHomeCareUserFilters =
    (searchingFor === SEOSegmentationSearchingFor.PROVIDER ||
      searchingFor === SEOSegmentationSearchingFor.ALL_PROVIDER) &&
    serviceIdForMember === ServiceIdForMember.HOUSEKEEPING;

  const handleCloseButton = () => {
    if (setOpenFiltersPopover) {
      setOpenFiltersPopover(false);
    }
    if (setOpenFiltersModal) {
      setOpenFiltersModal(false);
    }
    if (setPopoverAnchorEl) {
      setPopoverAnchorEl(null);
    }
  };

  const handleResetClick = () => {
    clearFilters();
    fetchResults({ zipcode: filters?.location?.zipcode }, true);
    handleCloseButton();
  };

  const getCurrentFilters = () => {
    const [minHourlyRate, maxHourlyRate] = hourlyRate;
    const currentHourlyRate = `${minHourlyRate}-${maxHourlyRate}`;

    if (showPetCareUserFilters) {
      return {
        hourlyRate: currentHourlyRate,
      };
    }
    if (showSeniorCareUserFilters) {
      return {
        hourlyRate: currentHourlyRate,
        seniorCareSubServices,
        ...seniorCareProfessionalSkillsObject,
        ...preferencesObject,
        languages: languagesSpoken,
      };
    }
    if (showChildCareUserFilters) {
      return {
        hourlyRate: currentHourlyRate,
        numberOfChildren,
        childCareAgeGroups: childCareAgeGroupsList,
        childCareQualifications: childCareProfessionalSkillsList,
        ...preferencesObject,
        languages: languagesSpoken,
      };
    }
    if (showHomeCareUserFilters) {
      return {
        hourlyRate: currentHourlyRate,
        housekeepingSubServices,
      };
    }

    return {};
  };

  const getCurrentVertical = () => {
    if (showPetCareUserFilters) {
      return 'PET_CARE';
    }
    if (showSeniorCareUserFilters) {
      return 'SENIOR_CARE';
    }
    if (showChildCareUserFilters) {
      return 'CHILD_CARE';
    }
    if (showHomeCareUserFilters) {
      return 'HOME_CARE';
    }

    return '';
  };

  const handleApplyClick = () => {
    const currentFilters = getCurrentFilters();
    const currentVertical = getCurrentVertical();
    const amplitudeFilters = getAmplitudeFilters(currentFilters, currentVertical);

    // save filters to local storage
    saveFilters(currentFilters);

    if (setPopoverAnchorEl) {
      setPopoverAnchorEl(null);
    }
    if (setOpenFiltersPopover) {
      setOpenFiltersPopover(false);
    }
    if (setOpenFiltersModal) {
      setOpenFiltersModal(false);
    }

    fetchResults({ zipcode: filters?.location?.zipcode, ...currentFilters }, true);
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, amplitudeFilters);
  };

  const closeButton = (
    <Box mb={3.5} textAlign="right">
      <IconButton
        onClick={handleCloseButton}
        aria-label="filter-close-btn"
        data-testid="close-filters-button"
        size="large">
        <Icon24UtilityClose />
      </IconButton>
    </Box>
  );

  const applyButton = (
    <Button
      color="secondary"
      variant="contained"
      size="large"
      sx={(theme) => ({
        height: '40px',
        minWidth: { xs: '100%', sm: '327px' },
        margin: '0 auto',
        fontSize: theme.typography.body1.fontSize,
      })}
      onClick={handleApplyClick}>
      Apply
    </Button>
  );

  return (
    <Container
      maxWidth="md"
      disableGutters={isMobile}
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        [theme.breakpoints.up('md')]: {
          width: '100%',
          maxWidth: '578px',
          paddingTop: theme.spacing(2.5),
          paddingBottom: theme.spacing(2),
        },
      })}>
      <Grid container direction="column">
        <Grid container item alignItems="center" justifyContent="space-between">
          <Grid container item xs={11} alignItems="center" justifyContent="space-between">
            <Box mb={3.5} display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h2">Filters</Typography>
              <Button
                color="primary"
                size="small"
                sx={{ width: '60px' }}
                onClick={handleResetClick}>
                <Typography variant="body2">Reset</Typography>
              </Button>
            </Box>
          </Grid>
          <Grid item xs={1}>
            {closeButton}
          </Grid>
        </Grid>
        {showPetCareUserFilters && (
          <Grid item>
            <PetCareFilters hourlyRate={hourlyRate} setHourlyRate={setHourlyRate} />
          </Grid>
        )}
        {showSeniorCareUserFilters && (
          <Grid item>
            <SeniorCareFilters
              hourlyRate={hourlyRate}
              setHourlyRate={setHourlyRate}
              seniorCareSubServices={seniorCareSubServices}
              setSeniorCareSubServices={setSeniorCareSubServices}
              setProfessionalSkillsObject={setSeniorCareProfessionalSkillsObject}
              setPreferencesObject={setPreferencesObject}
              languagesSpoken={languagesSpoken}
              setLanguagesSpoken={setLanguagesSpoken}
            />
          </Grid>
        )}
        {showChildCareUserFilters && (
          <Grid item>
            <ChildCareFilters
              hourlyRate={hourlyRate}
              setHourlyRate={setHourlyRate}
              numberOfChildren={numberOfChildren}
              setNumberOfChildren={setNumberOfChildren}
              childCareAgeGroupsList={childCareAgeGroupsList}
              setChildCareAgeGroupsList={setChildCareAgeGroupsList}
              childCareProfessionalSkillsList={childCareProfessionalSkillsList}
              setChildCareProfessionalSkillsList={setChildCareProfessionalSkillsList}
              setPreferencesObject={setPreferencesObject}
              languagesSpoken={languagesSpoken}
              setLanguagesSpoken={setLanguagesSpoken}
            />
          </Grid>
        )}
        {showHomeCareUserFilters && (
          <Grid item>
            <HomeCareFilters
              hourlyRate={hourlyRate}
              setHourlyRate={setHourlyRate}
              housekeepingSubServices={housekeepingSubServices}
              setHousekeepingSubServices={setHousekeepingSubServices}
            />
          </Grid>
        )}
      </Grid>
      {applyButton}
    </Container>
  );
};

export { Filters };
